import type {JSONNode} from '@packages/shared/types/JSONNode';
import * as JsonDomUtils from '@packages/systems/core/utils/JsonDomUtils';
import {getComponentRootElement} from '@packages/utilities/core-elements';
import {Component, Plugins} from '@packages/systems/wfdl';
import {qualify} from '@packages/systems/wfdl-Qualified';
import type {TranslatedNodeData} from '@packages/systems/localization-types';
import {getTranslatableData} from './getTranslatableData';

const SITE_PLUGIN_NAME = Plugins.name('__SitePlugin');

/**
 * Extracts translatable definition text for component roots not reachable
 * from `domNodes` — e.g. a component placed only inside a CMS Rich Text
 * field, whose definition still ends up in this page's `symbols` but is
 * never visited by the page walk. Skips library-origin components, since
 * their definition isn't this site's to translate.
 */
export function getComponentDefinitionTranslatableData(
  symbols: JSONNode[],
  domNodes: JSONNode[],
  components: Component.ComponentMap
): TranslatedNodeData[] {
  const isUsedOnPage = JsonDomUtils.isUsedSymbol(symbols, domNodes);
  const offPageRoots = symbols.filter(
    (symbol) => symbol.dataType && !isUsedOnPage(symbol)
  );

  const result: TranslatedNodeData[] = [];
  const seen = new Set<string>();

  for (const root of offPageRoots) {
    const componentType = qualify(SITE_PLUGIN_NAME, Component.name(root._id));
    const component = components.get(componentType);

    if (!component || Component.getOrigin(component)) {
      continue;
    }

    const rootElement = getComponentRootElement(components, componentType);
    if (!rootElement) {
      continue;
    }

    for (const item of getTranslatableData(rootElement, [], components)) {
      // A nested definition is reached twice — through its parent's walk and
      // as its own root — and identical strings are billed per occurrence on
      // a cold cache, so keep one copy. `path` is part of the identity: one
      // element can yield several attribute items that differ only by it
      // (e.g. a FormButton's `value` and `data-wait`).
      const key = [
        item._id,
        item.type ?? '',
        item.fieldId ?? '',
        item.componentId ?? '',
        item.translatableParentId,
        item.path ? JSON.stringify(item.path) : '',
      ].join('|');

      if (seen.has(key)) {
        continue;
      }

      seen.add(key);
      result.push(item);
    }
  }

  return result;
}
